import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReferralAdminOverview, ReferralAdminPartnerDetail } from '@zeroth/referral-contract';
import { ApiError, apiRequest } from '../lib/api';

// Access is decided by the Zeroth API's staff allowlist; the portal only shows what it is served.
const overviewKey = (userId: string | null | undefined) => ['partner-admin', 'overview', userId];
const partnerKey = (userId: string | null | undefined, id: string) => ['partner-admin', 'partner', userId, id];
const retry = (count: number, error: unknown) => !(error instanceof ApiError && error.status < 500) && count < 2;

export const isStaffOnly = (error: unknown) => error instanceof ApiError && error.status === 403;

export function useAdminOverview() {
  const { userId, getToken } = useAuth();
  return useQuery({
    queryKey: overviewKey(userId),
    enabled: !!userId,
    retry,
    queryFn: async () =>
      (await apiRequest<{ data: ReferralAdminOverview }>(getToken, 'GET', '/api/referrals/admin/overview')).data,
  });
}

export function useAdminPartner(id: string) {
  const { userId, getToken } = useAuth();
  return useQuery({
    queryKey: partnerKey(userId, id),
    enabled: !!userId,
    retry,
    queryFn: async () =>
      (
        await apiRequest<{ data: ReferralAdminPartnerDetail }>(
          getToken,
          'GET',
          `/api/referrals/admin/partners/${encodeURIComponent(id)}`
        )
      ).data,
  });
}

/** Records a payout already sent outside Zeroth. The API checks the minimum and the balance. */
export function useRecordPayout(id: string) {
  const { userId, getToken } = useAuth();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { amountCents: number; reference: string }) =>
      (
        await apiRequest<{ data: ReferralAdminPartnerDetail }>(
          getToken,
          'POST',
          `/api/referrals/admin/partners/${encodeURIComponent(id)}/payouts`,
          input
        )
      ).data,
    onSuccess: (data) => {
      client.setQueryData(partnerKey(userId, id), data);
      void client.invalidateQueries({ queryKey: overviewKey(userId) });
    },
  });
}
